/**
 * Plan Details Drawer
 *
 * Right-side slide-over showing the full detail of a subscription plan:
 * capacity, feature flags, billing terms and the audit trail.
 *
 * Layout notes: capacity numbers are the primary content, so they are set
 * large and tabular. Feature flags are rendered as a chip cloud rather than
 * one row per flag — twelve label/value rows scan as a wall of text.
 */

import { useCallback, useMemo, useState } from 'react';
import {
  CreditCard,
  Check,
  Minus,
  History,
  Pencil,
  Tag,
  Users,
  UserRound,
  FileText,
  FileCheck2,
  MessageSquare,
  HardDrive,
} from 'lucide-react';

import { Drawer, DrawerTitle, Badge, Button, AuditTimeline } from '@/components/common';
import { auditService } from '@/api/services';
import { cn } from '@/utils/cn';
import type { SubscriptionPlan, SubscriptionPlanTermGrid } from '@/api/types';

// ============================================
// Types
// ============================================

interface PlanDetailsDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  plan: SubscriptionPlan | null;
  /** Whether the current user can manage plans / terms */
  canManagePlans?: boolean;
  onEditPlan?: (plan: SubscriptionPlan) => void;
  onAddTerm?: (plan: SubscriptionPlan) => void;
}

/** Feature flags, in the order they appear in the chip cloud. */
const FEATURE_FLAGS: { label: string; key: keyof SubscriptionPlan }[] = [
  { label: 'Mobile App', key: 'mobileAppFeatureEnabled' },
  { label: 'SMS', key: 'smsFeatureEnabled' },
  { label: 'E-Invoice', key: 'eInvoiceEnabled' },
  { label: 'Custom Report Templates', key: 'customReportTemplatesEnabled' },
  { label: 'Live Tracking', key: 'liveTrackingEnabled' },
  { label: 'Tracking Photos', key: 'liveTrackingPhotosEnabled' },
  { label: 'Geo Fencing', key: 'geoFencingEnabled' },
  { label: 'Biometric Attendance', key: 'biometricAttendanceEnabled' },
  { label: 'Selfie Check-In', key: 'selfieCheckInEnabled' },
  { label: 'Personal Vault', key: 'personalVaultEnabled' },
  { label: 'Work Logs', key: 'workLogsEnabled' },
  { label: 'Tickets', key: 'ticketsEnabled' },
];

// ============================================
// Presentational Helpers
// ============================================

function Section({
  title,
  aside,
  children,
}: {
  title: string;
  aside?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-3">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-2xs font-semibold uppercase tracking-widest text-foreground-muted dark:text-foreground-dark-muted">
          {title}
        </h3>
        {aside}
      </div>
      {children}
    </section>
  );
}

/** A capacity limit: the number leads, the label supports it. */
function StatTile({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ElementType;
  label: string;
  value: number;
}) {
  return (
    <div className="rounded-xl border border-border bg-surface/70 p-3 dark:border-border-dark dark:bg-secondary-900/30">
      <div className="flex items-center gap-1.5 text-foreground-muted dark:text-foreground-dark-muted">
        <Icon className="h-3.5 w-3.5" />
        <span className="text-2xs font-medium uppercase tracking-wide">{label}</span>
      </div>
      <p className="mt-1.5 text-2xl font-semibold tabular-nums leading-none text-foreground dark:text-foreground-dark">
        {value.toLocaleString()}
      </p>
    </div>
  );
}

/** A feature flag. The check mark carries the signal; the chip stays neutral. */
function FeatureChip({ label, enabled }: { label: string; enabled: boolean }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs',
        enabled
          ? 'border-border bg-card font-medium text-foreground dark:border-border-dark dark:bg-secondary-800/60 dark:text-foreground-dark'
          : 'border-dashed border-border bg-transparent text-foreground-muted dark:border-border-dark dark:text-foreground-dark-muted'
      )}
    >
      {enabled ? (
        <Check className="h-3.5 w-3.5 shrink-0 text-success" />
      ) : (
        <Minus className="h-3.5 w-3.5 shrink-0 opacity-50" />
      )}
      {label}
    </span>
  );
}

function formatDuration(term: SubscriptionPlanTermGrid): string {
  const unit = term.termType?.toUpperCase() === 'YEAR' ? 'Year' : 'Month';
  const plural = term.termValue > 1 ? 's' : '';
  return `${term.termValue} ${unit}${plural}`;
}

/** A billing term. Price leads; the list price only shows when discounted. */
function TermCard({ term }: { term: SubscriptionPlanTermGrid }) {
  const discount =
    term.actualPrice > term.price && term.actualPrice > 0
      ? Math.round((1 - term.price / term.actualPrice) * 100)
      : 0;

  return (
    <div className="rounded-xl border border-border p-3.5 transition-colors hover:border-secondary-300 dark:border-border-dark dark:hover:border-secondary-600">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold text-foreground dark:text-foreground-dark">
            {formatDuration(term)}
          </p>
          <p className="truncate font-mono text-2xs text-foreground-muted dark:text-foreground-dark-muted">
            {term.planTermCode}
          </p>
        </div>
        <div className="shrink-0 text-right">
          <p className="text-lg font-semibold tabular-nums leading-none text-foreground dark:text-foreground-dark">
            {term.price?.toLocaleString()}
          </p>
          {discount > 0 && (
            <p className="mt-1 text-2xs tabular-nums text-foreground-muted line-through dark:text-foreground-dark-muted">
              {term.actualPrice.toLocaleString()}
            </p>
          )}
        </div>
      </div>
      <div className="mt-3 flex items-center gap-2 border-t border-border pt-2.5 dark:border-border-dark">
        <Badge variant={term.isActive ? 'success' : 'default'} size="sm" dot>
          {term.isActive ? 'Active' : 'Inactive'}
        </Badge>
        {discount > 0 && (
          <span className="text-2xs font-medium text-success">Save {discount}%</span>
        )}
      </div>
    </div>
  );
}

// ============================================
// Drawer Component
// ============================================

export function PlanDetailsDrawer({
  open,
  onOpenChange,
  plan,
  canManagePlans = false,
  onEditPlan,
  onAddTerm,
}: PlanDetailsDrawerProps) {
  const [auditOpen, setAuditOpen] = useState(false);

  // The grid returns auditId per plan; fall back to the plan id (same pattern
  // as OrganizationDetailsPage) so the timeline still works if it is absent.
  const auditId = plan?.auditId ?? plan?.id;

  const fetchAuditLogs = useCallback(
    (page: number, pageSize: number) => auditService.getAuditLogs(auditId!, page, pageSize),
    [auditId]
  );

  const features = useMemo(
    () =>
      plan
        ? FEATURE_FLAGS.map((f) => ({ ...f, enabled: Boolean(plan[f.key]) })).sort(
            (a, b) => Number(b.enabled) - Number(a.enabled)
          )
        : [],
    [plan]
  );

  if (!plan) return null;

  const terms = plan.planTerms ?? [];
  const enabledCount = features.filter((f) => f.enabled).length;

  const capacity = [
    { label: 'Max Users', value: plan.maxUsers, icon: Users },
    { label: 'Max Employees', value: plan.maxEmployees, icon: UserRound },
    { label: 'Max Contracts', value: plan.maxContracts, icon: FileText },
    { label: 'Active Contracts', value: plan.maxActiveContracts, icon: FileCheck2 },
    { label: 'SMS Credits', value: plan.totalSmsCredits, icon: MessageSquare },
    { label: 'Vault Size', value: plan.maxPersonalVaultSize, icon: HardDrive },
  ].flatMap((s) =>
  typeof s.value === "number"
    ? [{ ...s, value: s.value }]
    : []
);

  return (
    <>
      <Drawer
        open={open}
        onOpenChange={onOpenChange}
        size="xl"
        header={
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary-100 dark:bg-primary-900/30">
              <CreditCard className="h-5 w-5 text-primary" />
            </div>
            <div className="min-w-0">
              <DrawerTitle className="truncate text-lg font-semibold leading-tight text-foreground dark:text-foreground-dark">
                {plan.name}
              </DrawerTitle>
              <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="font-mono text-2xs text-foreground-muted dark:text-foreground-dark-muted">
                  {plan.code}
                </span>
                <span className="text-foreground-muted dark:text-foreground-dark-muted">·</span>
                <Badge variant={plan.status === 'ACTIVE' ? 'success' : 'default'} size="sm" dot>
                  {plan.status}
                </Badge>
                <Badge variant={plan.isCustomPlan ? 'warning' : 'outline'} size="sm">
                  {plan.isCustomPlan ? 'Custom' : 'Standard'}
                </Badge>
              </div>
            </div>
          </div>
        }
        footer={
          <>
            {auditId && (
              <Button
                variant="ghost"
                leftIcon={<History className="h-4 w-4" />}
                onClick={() => setAuditOpen(true)}
              >
                Audit Logs
              </Button>
            )}
            {canManagePlans && (
              <>
                <Button
                  variant="secondary"
                  leftIcon={<Tag className="h-4 w-4" />}
                  onClick={() => onAddTerm?.(plan)}
                >
                  Add Term
                </Button>
                <Button
                  leftIcon={<Pencil className="h-4 w-4" />}
                  onClick={() => onEditPlan?.(plan)}
                >
                  Edit Plan
                </Button>
              </>
            )}
          </>
        }
      >
        <div className="space-y-7">
          {plan.description && (
            <p className="text-sm leading-relaxed text-foreground-muted dark:text-foreground-dark-muted">
              {plan.description}
            </p>
          )}

          <Section title="Capacity">
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
              {capacity.map((stat) => (
                <StatTile
                  key={stat.label}
                  icon={stat.icon}
                  label={stat.label}
                  value={stat.value}
                />
              ))}
            </div>
          </Section>

          <Section
            title="Features"
            aside={
              <span className="text-xs tabular-nums text-foreground-muted dark:text-foreground-dark-muted">
                {enabledCount} of {features.length} enabled
              </span>
            }
          >
            <div className="flex flex-wrap gap-2">
              {features.map((f) => (
                <FeatureChip key={f.key} label={f.label} enabled={f.enabled} />
              ))}
            </div>
          </Section>

          <Section
            title="Billing Terms"
            aside={
              terms.length > 0 ? (
                <span className="text-xs tabular-nums text-foreground-muted dark:text-foreground-dark-muted">
                  {terms.length}
                </span>
              ) : undefined
            }
          >
            {terms.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border px-4 py-6 text-center dark:border-border-dark">
                <p className="text-sm text-foreground-muted dark:text-foreground-dark-muted">
                  No billing terms yet.
                </p>
                {canManagePlans && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-2"
                    leftIcon={<Tag className="h-4 w-4" />}
                    onClick={() => onAddTerm?.(plan)}
                  >
                    Add the first term
                  </Button>
                )}
              </div>
            ) : (
              <div className="grid gap-2.5 sm:grid-cols-2">
                {terms.map((term) => (
                  <TermCard key={term.id} term={term} />
                ))}
              </div>
            )}
          </Section>

        </div>
      </Drawer>

      {/* Audit trail */}
      {auditId && (
        <AuditTimeline
          open={auditOpen}
          onClose={() => setAuditOpen(false)}
          title={`${plan.name} Audit Logs`}
          fetchLogs={fetchAuditLogs}
        />
      )}
    </>
  );
}

export default PlanDetailsDrawer;
